import { Layout } from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { JsonLd, breadcrumbSchema, reviewsSchema } from "@/components/JsonLd";
import { Star, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_HREF, PHONE_NUMBER } from "@/components/EmergencyBanner";

const reviews = [
  { name: "Mike R.", location: "Stroudsburg, PA", text: "Called at 6 AM with no water. They were at my house by 8 and had everything running by noon. Incredible service. Fair price, honest assessment, and they cleaned up after themselves.", rating: 5 },
  { name: "Sarah T.", location: "Mount Pocono, PA", text: "Honest, fair pricing, and they explained everything before starting work. Our well pump was 12 years old and dying. They gave us options and didn't push the most expensive one. Won't use anyone else.", rating: 5 },
  { name: "Jim K.", location: "Brodheadsville, PA", text: "Our well pump died in January during a cold snap. They came out same day in terrible weather. Had a new pump on the truck and installed it that afternoon. True professionals.", rating: 5 },
  { name: "Linda P.", location: "Tobyhanna, PA", text: "Installed a new filtration system for our well water. The difference is night and day — no more sulfur smell, no more staining. They tested the water and recommended exactly what we needed.", rating: 5 },
  { name: "Bob M.", location: "East Stroudsburg, PA", text: "Grinder pump alarm went off at midnight. Called first thing in the morning and they were here by 9. Turned out to be a float switch. Fixed in under an hour. Reasonable price.", rating: 5 },
  { name: "Karen W.", location: "Effort, PA", text: "We've used Semper Fi for three different issues over the years. Every time, they're professional, on time, and fair. It's rare to find a contractor you can truly trust.", rating: 5 },
  { name: "Dave S.", location: "Tannersville, PA", text: "Low water pressure for months. Other companies said we needed a new well. Semper Fi found the real problem — a failing pressure tank. Saved us thousands. Honest people.", rating: 5 },
  { name: "Nancy L.", location: "Bushkill, PA", text: "New homeowners with a well for the first time. They walked us through everything, explained how the system works, and set up our filtration. Patient and knowledgeable.", rating: 5 },
];

const Reviews = () => (
  <Layout>
    <SeoHead
      title="Customer Reviews & Testimonials | Semper Fi Pump Service"
      description="Read real reviews from Pocono Mountains homeowners. See why 450+ customers rate Semper Fi 5 stars for well pump, grinder pump, and water system service."
    />
    <JsonLd
      data={[
        reviewsSchema(reviews),
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Reviews", path: "/reviews" },
        ]),
      ]}
    />
    <section className="bg-primary">
      <div className="container py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-black text-primary-foreground mb-4 border-l-0 pl-0">Customer Reviews</h1>
        <p className="text-primary-foreground/80 max-w-2xl text-pretty">See why homeowners across the Pocono Mountains trust Semper Fi for their well pump and water system needs.</p>
        <div className="flex items-center gap-3 mt-6">
          <div className="flex gap-1">
            {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-accent text-accent" />)}
          </div>
          <span className="text-primary-foreground font-bold tabular-nums">5.0</span>
          <span className="text-primary-foreground/60 text-sm">Based on 450+ 5-star reviews</span>
        </div>
        <p className="mt-6">
          <a
            href="https://www.google.com/search?sca_esv=22f559b5d064249e&rlz=1C5CHFA_enUS893US893&si=AL3DRZHrmvnFAVQPOO2Bzhf8AX9KZZ6raUI_dT7DG_z0kV2_x8jfY2Of2QDen24A0jPIdJX6Xi_QmAtCaeKlg4nXSg8mhBkcjSYuNSf7NBcyLaK0EhqKuZxMExmOlt7uVoNuyuI9nhdL3WbUdboB-jKMxSz3kFYDwg%3D%3D&q=Semper+Fi+Well+%26+Pump+Services+Reviews&sa=X&ved=2ahUKEwjWobX3uY6UAxWtMlkFHUIiML0Q0bkNegQIQBAH&biw=1675&bih=934&dpr=1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-semibold text-accent hover:text-accent/80 underline underline-offset-4 transition-colors"
          >
            Read all of our reviews on Google →
          </a>
        </p>
      </div>
    </section>


    <section className="container py-16 md:py-24">
      <div className="grid md:grid-cols-2 gap-6">
        {reviews.map((review, i) => (
          <div key={i} className="bg-card rounded-sm p-6 shadow-card">
            <div className="flex gap-1 mb-3">
              {Array.from({ length: review.rating }).map((_, j) => (
                <Star key={j} className="w-4 h-4 fill-accent text-accent" />
              ))}
            </div>
            <p className="text-sm text-foreground text-pretty mb-4 italic leading-relaxed">"{review.text}"</p>
            <div className="text-sm">
              <span className="font-semibold text-foreground">{review.name}</span>
              <span className="text-muted-foreground"> — {review.location}</span>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="bg-primary">
      <div className="container py-16 text-center">
        <h2 className="text-2xl font-black text-primary-foreground mb-4 border-l-0 pl-0 inline-block">Join Our Satisfied Customers</h2>
        <p className="text-primary-foreground/80 mb-8">Call now and experience the Semper Fi difference.</p>
        <Button asChild variant="emergency" size="xl">
          <a href={PHONE_HREF}><Phone className="w-5 h-5" />Call {PHONE_NUMBER}</a>
        </Button>
      </div>
    </section>
  </Layout>
);

export default Reviews;
